import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Shield, UserPlus, Check, AlertCircle } from 'lucide-react';
import api from '../../services/api';

/**
 * Add User Permission Modal
 * Allows adding users from the same sponsor with specific permission levels
 */
const AddUserPermissionModal = ({ isOpen, onClose, assessmentId, onUserAdded }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [usersByRole, setUsersByRole] = useState({});
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [permissionLevel, setPermissionLevel] = useState('edit');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sponsorName, setSponsorName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Permission level options
  const permissionLevels = [
    {
      value: 'full_admin',
      label: 'Full Admin',
      description: 'Complete control - can edit, publish, delete, and manage users',
      color: 'purple',
      icon: Shield
    },
    {
      value: 'edit',
      label: 'Edit Access',
      description: 'Can edit questions, screens, and content but cannot publish or delete',
      color: 'blue',
      icon: UserPlus
    },
    {
      value: 'view',
      label: 'View Only',
      description: 'Can view assessment but cannot make changes',
      color: 'gray',
      icon: Check
    }
  ];

  // Fetch available users when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAvailableUsers();
      setSelectedUsers(new Set());
      setPermissionLevel('edit');
      setError('');
      setSuccess('');
    }
  }, [isOpen, assessmentId]);

  // Filter users based on search and role filter
  useEffect(() => {
    let filtered = availableUsers;
    
    // Apply role filter first
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }
    
    // Then apply search filter
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.email.toLowerCase().includes(term) ||
          `${user.first_name} ${user.last_name}`.toLowerCase().includes(term) ||
          user.role.toLowerCase().includes(term)
      );
    }
    
    setFilteredUsers(filtered);
  }, [searchTerm, availableUsers, roleFilter]);

  const fetchAvailableUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${assessmentId}/available_users/`);
      const data = response.data;
      setAvailableUsers(data.users || []);
      setFilteredUsers(data.users || []);
      setUsersByRole(data.users_by_role || {});
      setSponsorName(data.sponsor_name || '');
      setRoleFilter('all');
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSubmit = async () => {
    if (selectedUsers.size === 0) {
      setError('Please select at least one user');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(
        `/api/assessment-builder/assessments/${assessmentId}/add_users/`,
        {
          user_ids: Array.from(selectedUsers),
          permission_level: permissionLevel
        }
      );
      
      setSuccess(`Successfully added ${selectedUsers.size} user(s) with ${permissionLevel.replace('_', ' ')} access`);
      
      // Clear selections
      setSelectedUsers(new Set());
      setSearchTerm('');
      
      // Callback to refresh usage data
      if (onUserAdded) {
        onUserAdded();
      }
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error adding users:', err);
      setError(err.message || 'Failed to add users. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedLevel = permissionLevels.find(p => p.value === permissionLevel);
  const LevelIcon = selectedLevel?.icon || Shield;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 dark:bg-black/70 z-[60] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <UserPlus className="w-6 h-6 text-blue-600" />
                  Add Users to Assessment
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Grant access to users from your sponsor organization
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Content — the permission picker and search stay pinned; only the
              user list scrolls, so there is a single scrollbar instead of a
              nested outer+inner pair (which felt wonky). */}
          <div className="flex-1 min-h-0 overflow-hidden p-6 flex flex-col gap-6">
            {/* Permission Level Selection */}
            <div className="flex-shrink-0">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Select Permission Level
              </label>
              <div className="grid grid-cols-3 gap-3">
                {permissionLevels.map((level) => {
                  const Icon = level.icon;
                  const isSelected = permissionLevel === level.value;
                  return (
                    <button
                      key={level.value}
                      onClick={() => setPermissionLevel(level.value)}
                      className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                        isSelected
                          ? `border-${level.color}-500 bg-${level.color}-50 shadow-md`
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      {isSelected && (
                        <div className={`absolute top-2 right-2 w-5 h-5 rounded-full bg-${level.color}-500 flex items-center justify-center`}>
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <Icon className={`w-5 h-5 mb-2 ${isSelected ? `text-${level.color}-600` : 'text-gray-400'}`} />
                      <h4 className={`font-semibold text-sm mb-1 ${isSelected ? `text-${level.color}-900` : 'text-gray-900'}`}>
                        {level.label}
                      </h4>
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {level.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Search and Filter Users */}
            <div className="flex-shrink-0">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Search Users {sponsorName && <span className="font-normal text-gray-500">from {sponsorName}</span>}
              </label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name or email..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm min-w-[160px]"
                >
                  <option value="all">All Roles ({availableUsers.length})</option>
                  {Object.keys(usersByRole).sort().map(role => (
                    <option key={role} value={role}>
                      {role} ({usersByRole[role]?.length || 0})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* User List — flexes to fill the remaining modal height and owns the
                only scroll region in the body. */}
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                <label className="block text-sm font-semibold text-gray-700">
                  Select Users ({selectedUsers.size} selected)
                </label>
                {selectedUsers.size > 0 && (
                  <button
                    onClick={() => setSelectedUsers(new Set())}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Clear selection
                  </button>
                )}
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
                  <p className="text-gray-600 mt-3 text-sm">Loading users...</p>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <AlertCircle className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">
                    {searchTerm ? 'No users found matching your search' : 'No available users'}
                  </p>
                </div>
              ) : (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg flex-1 min-h-0 overflow-y-auto">
                  {filteredUsers.map((user) => {
                    const isSelected = selectedUsers.has(user.id);
                    return (
                      <button
                        key={user.id}
                        onClick={() => toggleUserSelection(user.id)}
                        className={`w-full flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          isSelected ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1 text-left">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                            isSelected ? 'bg-blue-500' : 'bg-gradient-to-br from-blue-500 to-purple-500'
                          }`}>
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 truncate">
                              {user.first_name} {user.last_name}
                            </h4>
                            <p className="text-sm text-gray-600 truncate">{user.email}</p>
                            <p className="text-xs text-gray-500">{user.role_display || user.role}</p>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3 flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3 flex-shrink-0">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-700">{success}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <LevelIcon className="inline w-4 h-4 mr-1" />
                Granting <span className="font-semibold">{selectedLevel?.label}</span> to {selectedUsers.size} user(s)
              </div>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || selectedUsers.size === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Adding...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      Add Users
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddUserPermissionModal;
